import moment, { Moment } from 'moment';
import { RATEHAWK_API_LIMIT_VALUES } from './constants';

/**
 * Stay-length helpers for hotel / dynamic-package searches.
 *
 * Ratehawk rejects hotel searches where checkout is more than
 * `RATEHAWK_API_LIMIT_VALUES.max_days` (30) nights after checkin. The pickers
 * cap selection via `maxDaysLimit`, but dates can also arrive from URL params,
 * persisted state, or backoffice config — these helpers let submit-time
 * validation and the API hooks enforce the same limit before firing requests.
 */

const parseStayDate = (
  value: string | Moment | null | undefined,
  dateFormat?: string
): Moment | null => {
  if (value === null || value === undefined || value === '') return null;
  if (moment.isMoment(value)) return value.clone();

  const formats = dateFormat
    ? [dateFormat, 'YYYY-MM-DD', 'DD/MM/YYYY']
    : ['YYYY-MM-DD', 'DD/MM/YYYY'];
  const parsed = moment(value, formats, true);
  return parsed.isValid() ? parsed : null;
};

/**
 * Nights between two dates, or null when either date is missing/invalid
 * (missing dates are owned by the required-field validations).
 */
export const getStayNights = (
  from: string | Moment | null | undefined,
  to: string | Moment | null | undefined,
  dateFormat?: string
): number | null => {
  const start = parseStayDate(from, dateFormat);
  const end = parseStayDate(to, dateFormat);
  if (!start || !end) return null;
  return end.startOf('day').diff(start.startOf('day'), 'days');
};

/** True when the stay is longer than the Ratehawk 30-night limit. */
export const exceedsMaxStay = (
  from: string | Moment | null | undefined,
  to: string | Moment | null | undefined,
  dateFormat?: string,
  maxDays: number = RATEHAWK_API_LIMIT_VALUES.max_days
): boolean => {
  const nights = getStayNights(from, to, dateFormat);
  return nights !== null && nights > maxDays;
};
